import { EQpProfile } from '@library/models/qp-profile.models';
import { INTERCEPTOR_SKIP_HEADER } from '@one/app/shared/interceptors/inspection.interceptor';
import { AccountService } from '@one/app/shared/services/account/account.service';
import { HttpHandlerFn, HttpRequest, HttpEvent, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable } from 'rxjs';

/** Dummy base used to resolve relative request URLs (e.g. `api/inspections/1`) to inspect their `pathname`. */
const URL_RESOLUTION_BASE = 'https://qima-ngsw-bypass.invalid/';

/**
 * @description
 * Whether the request targets our backend, based on its resolved `pathname` (`/api/...`) rather than a
 * plain substring match, so paths like `ngx-qima/assets/api/icons/foo.svg` are correctly excluded.
 * @param {string} url the request URL, relative or absolute
 * @returns {boolean} true if this is a backend `/api/` call
 */
function isApiRequest(url: string): boolean {
  return new URL(url, URL_RESOLUTION_BASE).pathname.startsWith('/api/');
}

/**
 * @description
 * Ensures backend API calls never go through the Angular service worker proxy.
 *
 * `ngsw-config.json` has no API `dataGroups` — the SW only serves the app shell / static assets, so
 * this only touches `api/**` calls; static asset requests (e.g. ngx-qima icon SVGs) are left untouched
 * and stay servable/cacheable by the SW as intended. Offline API data for inspectors is handled by
 * IndexedDB domain interceptors, not by ngsw. Without `ngsw-bypass`, the SW still proxies every API
 * call (double Network entries: canceled xhr + ngsw fetch) and can abort requests under load
 * (e.g. invitee inspection preload).
 *
 * Non-inspectors also keep `X-Skip-Interceptor` so the inspection offline interceptor stack is skipped.
 * @see https://angular.dev/ecosystem/service-workers/devops#bypassing-the-service-worker
 * @see https://github.com/w3c/ServiceWorker/issues/1141
 * @param {HttpRequest} request the request to intercept
 * @param {HttpHandlerFn} next the request handler
 * @returns {Observable<HttpEvent<unknown>> | Observable<HttpResponse<undefined>>} the request or the response to return
 */
export function ngswBypassInterceptor$(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> | Observable<HttpResponse<undefined>> {
  if (!isApiRequest(request.url)) {
    return next(request);
  }

  const accountService: AccountService = inject(AccountService);
  const isInspector: boolean = accountService.hasAnyAuthority([EQpProfile.ROLE_INSPECTOR, EQpProfile.ROLE_SERVICE_PROVIDER_INSPECTOR]);
  let headers = request.headers;

  if (!headers.has('ngsw-bypass')) {
    headers = headers.set('ngsw-bypass', 'true');
  }

  if (!isInspector && !headers.has(INTERCEPTOR_SKIP_HEADER)) {
    headers = headers.set(INTERCEPTOR_SKIP_HEADER, 'true');
  }

  if (headers === request.headers) {
    return next(request);
  }

  return next(request.clone({ headers }));
}
